import React from 'react';
import { Loader2 } from 'src/components/ui/animated-loader2';
import { RequestStatusBadge } from 'src/features/requests/request-status-badge';
import { resolveDisplayedRequestStatus } from 'src/features/requests/request.helper';

// Structural shape — matches both `RequestsTableResponse` (table rows) and
// `RequestResponse` (preview sheet / detail page), whose status fields differ
// in nominal type (`RequestStatus` literal vs `string`) but read identically
// here. Aligns with `resolveDisplayedRequestStatus`.
type Props = {
  request: {
    status?: string | null;
    requested_attention_at?: string | null;
    requested_reedit_at?: string | null;
    reedit_count?: number | null;
  };
  size?: 'sm' | 'default';
};

/**
 * Status pill for a row on the admin requests table.
 *
 * The pill itself absorbs the "Needs Attention" / "Needs Re-edit" signal the
 * now-removed `status_details` column used to carry.
 *
 * A `Loader2` trails inside the pill while the row is in an ACTIVE re-edit
 * round. The signal is `requested_reedit_at`: be-core sets it when the customer
 * requests a re-edit and keeps it set through Needs Re-edit → Editing → Ready
 * for Review (RequestManager), clearing it on approval/undo. So the spinner
 * "only begins appearing" once the request hits Needs Re-edit and then trails
 * the round (#2468 Karina), exactly as #2442/#2443/#2462 intended.
 *
 * Two signals drive it, OR'd together:
 *  - the *active* `requested_reedit_at` — set while a round is in flight and
 *    kept through Needs Re-edit → Editing → Ready for Review, cleared on
 *    approval/undo; and
 *  - the *historical* `reedit_count` (be-core's lifetime counter, #2442) but
 *    ONLY while the row is "Ready for review" (`delivered`).
 *
 * The historical branch exists because Cancel/Undo clears `requested_reedit_at`
 * the instant a client starts then cancels a fresh re-edit, wiping the trail
 * off a row that genuinely completed an earlier cycle and is still awaiting
 * review (#2548). `reedit_count` survives that cancel (be-core never clears
 * it), so it restores the indicator. It is gated to `delivered` on purpose:
 * `reedit_count` stays >0 forever after any past re-edit, and ungated it lit
 * the spinner next to a plain "Queued" row (the DOOR #1 bug in #2468). Gating
 * to "Ready for review" keeps the #2468 fix while satisfying #2548.
 *
 * Suppressed while the pill shows "Needs Attention" — that overlay is
 * urgency-first and supersedes the re-edit spinner (#2462); the underlying
 * re-edit state is unchanged, so the loader resumes once attention clears.
 */
const RequestStatusCell: React.FC<Props> = ({ request, size = 'sm' }) => {
  const displayedStatus = resolveDisplayedRequestStatus(request);
  const showReeditLoader =
    (!!request.requested_reedit_at ||
      (!!request.reedit_count && request.status === 'delivered')) &&
    request.status !== 'complete' &&
    request.status !== 'archived' &&
    displayedStatus !== 'needs_attention';

  return (
    <RequestStatusBadge
      size={size}
      variant={displayedStatus}
      iconPrefix={
        showReeditLoader ? (
          // `unset` lets the spinner inherit the badge's text color so it tints
          // to whatever stage the round is in (red on Needs Re-edit, etc.). The
          // wrapper bakes in `tw-animate-spin`; shrink it to 12px to match the
          // inline-icon scale on the customer FE pill.
          <Loader2
            variant={'unset'}
            className={'tw-size-3 tw-shrink-0'}
            aria-hidden={'true'}
          />
        ) : undefined
      }
    />
  );
};

export { RequestStatusCell };
